/**
 * Analyzer: ATM Edit Suggestions (Phase 1 — Read-only)
 *
 * Compares a single ATM template's *parameters* against the empirical
 * MAE/MFE distribution of trades managed by that template, and emits
 * structured edit suggestions ("bump PT1 from 120t → 168t — 47 trades
 * show MFE averaging 1.4R, 40% past the current first target").
 *
 * Phase 1 is intentionally **read-only**. This module's contract is:
 *   suggestAtmEdits(atmName, parsedAtm, trades, opts) → EditSuggestion[]
 *
 * Phase 2 (inline editor) will consume these suggestions as starting
 * points for a stage-editor modal. Phase 3 (direct edit + backup) will
 * persist the edited XML back to NinjaTrader's template folder.
 *
 * Detection patterns this round:
 *
 *   1. PT-too-tight — winners consistently extend past the first target.
 *      Triggers when avg MFE (ticks) ≥ 120% of current PT1 AND n ≥ 10.
 *      Suggests new PT1 = round-to-tick(avg MFE × 0.85), so we capture
 *      most of the typical extension without overreaching.
 *
 *   2. PT-too-far — winners rarely reach the first target.
 *      Triggers when ≥70% of trades had MFE below PT1 AND n ≥ 15.
 *      Suggests new PT1 = round-to-tick(median MFE × 1.05), so the
 *      target sits at the achievable side of the distribution.
 *
 *   3. SL-too-tight — losers cluster near full stop after partial favorable.
 *      Triggers when avg loser MAE ≥ 85% of SL AND avg winner MAE ≥ 30%
 *      of SL (i.e., normal adverse excursion happens before working).
 *      Suggests new SL = round-to-tick(max loser MAE × 1.10), to give
 *      enough room for typical noise.
 *
 *   4. SL-too-wide — most stops never come close, so risk is overpaying.
 *      Triggers when 90th-percentile MAE (all trades) ≤ 50% of SL AND
 *      n ≥ 20. Suggests new SL = round-to-tick(p90 MAE × 1.20).
 *
 * Confidence tiers (for UI badge coloring):
 *   - 'high'   — n ≥ 30 AND signal is strongly off (≥ 30% from suggested)
 *   - 'medium' — n ≥ 15 AND signal is moderately off (15-30%)
 *   - 'low'    — n ≥ 10 (or strong but small sample)
 *
 * Pure function. No side effects. Easy to unit test.
 *
 * Reads:
 *   trade.atmStrategy (matches atmName)
 *   trade.symbol      (for tickSize lookup via futuresSpecs)
 *   trade.pnl, trade.netPnl (to classify winner/loser)
 *   trade.quantity    (for $ impact estimation)
 *   trade.barAnalysis.{maePoints, mfePoints}  (the empirical evidence)
 *
 * Skips trades without barAnalysis (no MAE/MFE to compare against).
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';
import { FUTURES_SPECS } from '../../../../config/futuresSpecs.js';

// Coach engine integration — the scope dispatcher (scopes/atm.js) reads
// `key` to label observations and `tier` to gate by user plan. 'free'
// means every user sees these suggestions; deeper Phase 2/3 editing UI
// can pro-gate at the surface layer.
export const key = 'atmEditSuggestions';
export const tier = 'free';

// ── Thresholds — tuned conservatively for Phase 1 ────────────────────
const MIN_SAMPLE_ANY              = 10;   // never suggest with fewer
const MIN_SAMPLE_HIGH_CONFIDENCE  = 30;
const MIN_SAMPLE_MED_CONFIDENCE   = 15;

const PT_TIGHT_AVG_MFE_RATIO      = 1.20;  // avg MFE ≥ 120% of PT1
const PT_FAR_BELOW_FRACTION       = 0.70;  // ≥70% of trades had MFE < PT1
const PT_FAR_MIN_SAMPLE           = 15;

const SL_TIGHT_AVG_LOSER_MAE_RATIO = 0.85;  // avg loser MAE ≥ 85% of SL
const SL_TIGHT_AVG_WINNER_MAE_MIN  = 0.30;  // AND avg winner MAE ≥ 30% of SL
const SL_TIGHT_MIN_LOSERS          = 6;

const SL_WIDE_P90_RATIO            = 0.50;  // p90 MAE ≤ 50% of SL
const SL_WIDE_MIN_SAMPLE           = 20;

// ── Pure helpers ─────────────────────────────────────────────────────

/** Get the symbol's base ticker (strip month/year suffix). MNQM6 → MNQ. */
const baseSymbol = (sym) => {
  if (!sym || typeof sym !== 'string') return '';
  const s = sym.trim().toUpperCase();
  // Common patterns: MNQ, MNQM6, MNQ 06-26, MNQ06-26
  const m = s.match(/^([A-Z]{1,4})(?:[ \-]?\d|$)/);
  return m ? m[1] : s;
};

/** Convert points to ticks given tickSize (returns rounded int). */
const pointsToTicks = (points, tickSize) => {
  if (!Number.isFinite(points) || !Number.isFinite(tickSize) || tickSize <= 0) return 0;
  return Math.round(points / tickSize);
};

/** Round a tick count to the nearest sensible step (4 for trial brackets). */
const roundTicks = (ticks) => {
  if (!Number.isFinite(ticks) || ticks <= 0) return 0;
  // Round to nearest 4 ticks (1 point on most index futures) — keeps
  // suggestions visually clean and avoids fractional-tick targets.
  return Math.max(4, Math.round(ticks / 4) * 4);
};

const median = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return 0;
  const sorted = arr.slice().sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

const percentile = (arr, p) => {
  if (!Array.isArray(arr) || arr.length === 0) return 0;
  const sorted = arr.slice().sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.floor(sorted.length * p));
  return sorted[idx];
};

const mean = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return 0;
  return arr.reduce((s, x) => s + x, 0) / arr.length;
};

/**
 * Classify confidence based on sample size and signal magnitude.
 * Signal magnitude = |current - suggested| / current.
 */
const classifyConfidence = (sampleSize, signalMagnitude) => {
  if (sampleSize >= MIN_SAMPLE_HIGH_CONFIDENCE && signalMagnitude >= 0.30) return 'high';
  if (sampleSize >= MIN_SAMPLE_MED_CONFIDENCE && signalMagnitude >= 0.15) return 'medium';
  return 'low';
};

// ── EditSuggestion factory ───────────────────────────────────────────

/**
 * @typedef {Object} EditSuggestion
 * @property {string}  id              Stable id for dedup
 * @property {string}  atmName         The ATM template name
 * @property {number}  bracketIndex    Which bracket (0-based)
 * @property {string}  contractLabel   Human label e.g. 'C1', 'C2'
 * @property {string}  field           Field path on bracket (e.g. 'targetTicks', 'stopLossTicks')
 * @property {string}  fieldLabel      Human-friendly field name e.g. 'PT1 (first take-profit)'
 * @property {number}  currentValue    Current setting in source units
 * @property {number}  suggestedValue  Recommended setting in source units
 * @property {string}  unit            Unit label e.g. 'ticks'
 * @property {number}  delta           suggestedValue - currentValue
 * @property {string}  reason          Short title-friendly headline
 * @property {string}  detail          Detail sentence with numbers
 * @property {string}  severity        'warning' | 'info'
 * @property {number}  sampleSize      Number of trades supporting the suggestion
 * @property {string}  confidence      'high' | 'medium' | 'low'
 * @property {string}  [expectedImpact] e.g. '+0.4R avg per trade'
 * @property {Object}  evidence        Raw stats for debugging / LLM narration
 */

const makeSuggestion = (fields) => {
  const {
    atmName, bracketIndex, contractLabel,
    field, fieldLabel,
    currentValue, suggestedValue, unit = 'ticks',
    reason, detail, severity = 'info',
    sampleSize, confidence = 'low',
    expectedImpact = null,
    evidence = null,
    suggestionType,
  } = fields;
  return {
    id: `${suggestionType}:${atmName}:b${bracketIndex}:${field}`,
    atmName,
    bracketIndex,
    contractLabel: contractLabel || `C${bracketIndex + 1}`,
    field,
    fieldLabel,
    currentValue,
    suggestedValue,
    unit,
    delta: suggestedValue - currentValue,
    reason,
    detail,
    severity,
    sampleSize,
    confidence,
    expectedImpact,
    evidence,
    suggestionType,
  };
};

// ── Per-bracket analysis ─────────────────────────────────────────────

/**
 * Analyze one bracket against the matched trade pool.
 * Returns 0..N suggestions for this bracket.
 *
 * Note: a single bracket can yield BOTH a PT suggestion and an SL
 * suggestion — they're independent dimensions of the same contract.
 */
const analyzeBracket = ({ bracket, trades, tickSize, atmName }) => {
  const suggestions = [];
  if (!bracket) return suggestions;

  // Build the per-trade tick arrays once. We don't filter to "trades that
  // actually used this bracket" because for Phase 1, every trade on the
  // ATM is evidence about how the *first* bracket's PT/SL placement
  // performs against the typical move. Multi-bracket ATMs get refined
  // logic in Phase 1.5 (matchExitsToBrackets-aware).
  const mfeTicks = [];
  const maeTicks = [];
  const loserMaeTicks = [];
  const winnerMaeTicks = [];
  for (const t of trades) {
    const ba = t?.barAnalysis;
    if (!ba) continue;
    if (!Number.isFinite(ba.mfePoints) || !Number.isFinite(ba.maePoints)) continue;
    const mfeT = pointsToTicks(ba.mfePoints, tickSize);
    const maeT = pointsToTicks(ba.maePoints, tickSize);
    mfeTicks.push(mfeT);
    maeTicks.push(maeT);
    const pnl = Number(t.netPnl ?? t.pnl ?? 0);
    if (pnl > 0) winnerMaeTicks.push(maeT);
    else if (pnl < 0) loserMaeTicks.push(maeT);
  }

  const n = mfeTicks.length;
  if (n < MIN_SAMPLE_ANY) return suggestions;

  // ── PT (target) analysis — only meaningful when this bracket has a fixed target
  if (bracket.exitType === 'takeProfit' && bracket.targetTicks > 0) {
    const ptCurrent = bracket.targetTicks;
    const avgMfe = mean(mfeTicks);
    const medMfe = median(mfeTicks);
    const belowPtCount = mfeTicks.filter(t => t < ptCurrent).length;
    const belowPtFraction = belowPtCount / n;

    // PT too tight — winners typically extend past target
    if (avgMfe >= ptCurrent * PT_TIGHT_AVG_MFE_RATIO) {
      const suggestedRaw = avgMfe * 0.85;  // capture most of the move, not the peak
      const suggested = roundTicks(suggestedRaw);
      if (suggested > ptCurrent) {
        const magnitude = Math.abs(suggested - ptCurrent) / ptCurrent;
        suggestions.push(makeSuggestion({
          suggestionType: 'pt-too-tight',
          atmName,
          bracketIndex: bracket.index,
          field: 'targetTicks',
          fieldLabel: bracket.index === 0
            ? 'PT1 (first take-profit target)'
            : `PT${bracket.index + 1} (take-profit, contract ${bracket.index + 1})`,
          currentValue: ptCurrent,
          suggestedValue: suggested,
          reason: `Winners extend ~${Math.round((avgMfe / ptCurrent - 1) * 100)}% past target`,
          detail: `Avg MFE across ${n} trades was ${Math.round(avgMfe)}t — your PT sits at ${ptCurrent}t.`,
          severity: 'warning',
          sampleSize: n,
          confidence: classifyConfidence(n, magnitude),
          expectedImpact: `Capturing the typical extension would add ~${Math.round((suggested - ptCurrent) * tickSize * 100) / 100} pts per first-touch winner.`,
          evidence: { avgMfe, medMfe, ptCurrent, sampleSize: n },
        }));
      }
    }

    // PT too far — most trades never reach target
    else if (belowPtFraction >= PT_FAR_BELOW_FRACTION && n >= PT_FAR_MIN_SAMPLE) {
      const suggestedRaw = medMfe * 1.05;
      const suggested = roundTicks(suggestedRaw);
      if (suggested > 0 && suggested < ptCurrent) {
        const magnitude = Math.abs(ptCurrent - suggested) / ptCurrent;
        suggestions.push(makeSuggestion({
          suggestionType: 'pt-too-far',
          atmName,
          bracketIndex: bracket.index,
          field: 'targetTicks',
          fieldLabel: bracket.index === 0
            ? 'PT1 (first take-profit target)'
            : `PT${bracket.index + 1}`,
          currentValue: ptCurrent,
          suggestedValue: suggested,
          reason: `Target rarely hit — ${Math.round(belowPtFraction * 100)}% of trades never reached PT`,
          detail: `Median MFE across ${n} trades was ${Math.round(medMfe)}t — target sits at ${ptCurrent}t.`,
          severity: 'info',
          sampleSize: n,
          confidence: classifyConfidence(n, magnitude),
          expectedImpact: 'A reachable target lifts hit-rate on partials; runners can still extend via trailing brackets.',
          evidence: { medMfe, ptCurrent, belowPtFraction, sampleSize: n },
        }));
      }
    }
  }

  // ── SL analysis — applies to every bracket that has a fixed stop
  if (bracket.stopLossTicks > 0) {
    const slCurrent = bracket.stopLossTicks;

    // SL too tight — losers cluster near full stop after partial favorable
    if (loserMaeTicks.length >= SL_TIGHT_MIN_LOSERS && winnerMaeTicks.length >= 4) {
      const avgLoserMae = mean(loserMaeTicks);
      const avgWinnerMae = mean(winnerMaeTicks);
      const loserRatio = avgLoserMae / slCurrent;
      const winnerRatio = avgWinnerMae / slCurrent;
      if (loserRatio >= SL_TIGHT_AVG_LOSER_MAE_RATIO && winnerRatio >= SL_TIGHT_AVG_WINNER_MAE_MIN) {
        // Use p95 of losers (not max) to ignore single-trade outliers. A single
        // 740-tick MAE trade shouldn't suggest a 816-tick stop on a 60-tick
        // ATM — that's terrible advice. And cap any widening at 2× current
        // stop so even pathological distributions produce sane suggestions.
        const p95LoserMae = percentile(loserMaeTicks, 0.95);
        const suggestedRaw = p95LoserMae * 1.10;
        const capped = Math.min(suggestedRaw, slCurrent * 2);
        const suggested = roundTicks(capped);
        if (suggested > slCurrent) {
          const magnitude = Math.abs(suggested - slCurrent) / slCurrent;
          suggestions.push(makeSuggestion({
            suggestionType: 'sl-too-tight',
            atmName,
            bracketIndex: bracket.index,
            field: 'stopLossTicks',
            // Most futures ATMs share a single hard stop across all contracts.
            // Label the FIRST-bracket suggestion as universal so the UI dedupe
            // step (see dedupeSuggestions) can collapse identical sister
            // suggestions on contracts 2/3/etc. into the one card.
            fieldLabel: 'Stop Loss (per contract)',
            currentValue: slCurrent,
            suggestedValue: suggested,
            reason: `Stop at ${slCurrent}t — losers reach ${Math.round(loserRatio * 100)}% of stop`,
            detail: `Across ${loserMaeTicks.length} losers, avg MAE was ${Math.round(avgLoserMae)}t (95th-pctile ${Math.round(p95LoserMae)}t). Winners showed avg ${Math.round(avgWinnerMae)}t adverse before working.`,
            severity: 'warning',
            sampleSize: loserMaeTicks.length + winnerMaeTicks.length,
            confidence: classifyConfidence(loserMaeTicks.length + winnerMaeTicks.length, magnitude),
            expectedImpact: `Loosening the stop reduces noise-driven stop-outs at the cost of larger per-loss size — net edge depends on resulting hit-rate.`,
            evidence: { avgLoserMae, avgWinnerMae, p95LoserMae, slCurrent, losers: loserMaeTicks.length, winners: winnerMaeTicks.length },
          }));
        }
      }
    }

    // SL too wide — almost never gets touched (overpaying for risk)
    if (n >= SL_WIDE_MIN_SAMPLE) {
      const p90Mae = percentile(maeTicks, 0.90);
      if (p90Mae > 0 && p90Mae <= slCurrent * SL_WIDE_P90_RATIO) {
        const suggestedRaw = p90Mae * 1.20;
        const suggested = roundTicks(suggestedRaw);
        if (suggested > 0 && suggested < slCurrent) {
          const magnitude = Math.abs(slCurrent - suggested) / slCurrent;
          suggestions.push(makeSuggestion({
            suggestionType: 'sl-too-wide',
            atmName,
            bracketIndex: bracket.index,
            field: 'stopLossTicks',
            fieldLabel: bracket.index === 0
              ? 'Stop Loss (per contract)'
              : `Stop Loss (contract ${bracket.index + 1})`,
            currentValue: slCurrent,
            suggestedValue: suggested,
            reason: `Stop at ${slCurrent}t but 90% of moves stay within ${p90Mae}t`,
            detail: `Across ${n} trades, the 90th-percentile adverse excursion was ${p90Mae}t. Tighter stop trims per-loss size without losing winners.`,
            severity: 'info',
            sampleSize: n,
            confidence: classifyConfidence(n, magnitude),
            expectedImpact: `Per-loss dollar risk drops proportionally (you'd save ~${Math.round((slCurrent - suggested) * tickSize * 100) / 100} pts of max risk).`,
            evidence: { p90Mae, slCurrent, sampleSize: n },
          }));
        }
      }
    }
  }

  return suggestions;
};

// ── Public API ───────────────────────────────────────────────────────

/**
 * Generate edit suggestions for one ATM template.
 *
 * @param {string} atmName        The template name (matches trade.atmStrategy)
 * @param {Object} parsedAtm      Output of `parseAtmXml(...)` — must have brackets[]
 * @param {Array}  trades         Trade history (will be filtered to atmName)
 * @param {Object} [opts]
 * @param {Function} [opts.tickSizeOverride]  (symbol) => tickSize. Default: futuresSpecs lookup.
 * @returns {EditSuggestion[]}
 */
export const suggestAtmEdits = (atmName, parsedAtm, trades, opts = {}) => {
  if (!atmName || typeof atmName !== 'string') return [];
  if (!parsedAtm || !Array.isArray(parsedAtm.brackets) || parsedAtm.brackets.length === 0) return [];
  if (!Array.isArray(trades)) return [];

  // Filter to trades on this ATM (case-insensitive trim to be forgiving)
  const atmKey = atmName.trim().toLowerCase();
  const matched = trades.filter(t => {
    const s = (t?.atmStrategy || '').trim().toLowerCase();
    return s === atmKey;
  });
  if (matched.length < MIN_SAMPLE_ANY) return [];

  // Resolve tickSize from the symbol of the first matched trade (or use opts override).
  // If trades span multiple symbols (rare for one ATM), fall back to the most common.
  const tickSizeLookup = typeof opts.tickSizeOverride === 'function'
    ? opts.tickSizeOverride
    : (sym) => FUTURES_SPECS[baseSymbol(sym)]?.tickSize ?? 0.25;
  // Pick the symbol most frequently traded under this ATM
  const symbolCounts = {};
  for (const t of matched) {
    const base = baseSymbol(t?.symbol);
    if (!base) continue;
    symbolCounts[base] = (symbolCounts[base] || 0) + 1;
  }
  const dominantSymbol = Object.entries(symbolCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
  const tickSize = tickSizeLookup(dominantSymbol) || 0.25;

  const suggestions = [];
  for (const bracket of parsedAtm.brackets) {
    suggestions.push(...analyzeBracket({ bracket, trades: matched, tickSize, atmName }));
  }

  // Dedupe — collapse identical (suggestionType, field, currentValue,
  // suggestedValue) tuples across brackets. Futures ATMs share a single
  // hard stop across all contracts, so a 5-contract ATM would otherwise
  // emit 5 identical SL warnings. The first occurrence wins; we relabel
  // its fieldLabel to make the universality explicit.
  const seen = new Map();
  for (const s of suggestions) {
    const dedupKey = `${s.suggestionType}:${s.field}:${s.currentValue}:${s.suggestedValue}`;
    if (!seen.has(dedupKey)) {
      seen.set(dedupKey, s);
    } else {
      // Mark the kept suggestion as covering all brackets so the UI can
      // hint "this advice applies to every contract on this ATM."
      const kept = seen.get(dedupKey);
      kept.bracketIndex = -1;  // -1 = all brackets
      kept.contractLabel = 'all contracts';
      if (!kept.fieldLabel.includes('all contracts')) {
        kept.fieldLabel = kept.fieldLabel.replace(/\s*\([^)]*\)\s*$/, '') + ' (all contracts)';
      }
    }
  }
  return Array.from(seen.values());
};

/**
 * Resolve a parsedAtm structure for the analyzer. Two callers:
 *   - Direct (tests, AtmTab inline render): caller passes `parsedAtm` explicitly
 *   - Coach scope dispatcher: caller passes `ntTemplateVersions` map; we read
 *     versions[atmName][0].snapshot which is already in the parsed shape.
 */
const resolveParsedAtm = ({ parsedAtm, ntTemplateVersions, atmName }) => {
  if (parsedAtm && Array.isArray(parsedAtm.brackets)) return parsedAtm;
  const versions = ntTemplateVersions?.[atmName];
  if (!Array.isArray(versions) || versions.length === 0) return null;
  const snap = versions[0]?.snapshot;
  if (!snap || !Array.isArray(snap.brackets)) return null;
  // Snapshot shape matches what parseAtmXml produces — brackets[] with the
  // same field names. Re-emit as parsedAtm so suggestAtmEdits sees what it
  // expects.
  return {
    name: snap.name || atmName,
    entryQuantity: snap.entryQuantity ?? 0,
    calculationMode: snap.calculationMode || 'Ticks',
    brackets: snap.brackets,
  };
};

/**
 * Coach-engine adapter — produces Observation objects suitable for the
 * Live Coach Feed / AI Coach panel. Accepts the ATM-scope context.
 *
 * @param {Object} ctx
 * @param {string} ctx.atmName               Template under analysis
 * @param {Array}  [ctx.atmTrades]           Already filtered + enriched trades on this ATM
 *                                            (from coach scope dispatcher).
 * @param {Array}  [ctx.allTrades]           Full trade history (used by direct callers).
 * @param {Object} [ctx.parsedAtm]           Direct parsedAtm override (used by tests / AtmTab)
 * @param {Object} [ctx.ntTemplateVersions]  NT template versions map (scope dispatcher path)
 * @returns {Observation[]}
 */
export const analyze = (ctx = {}) => {
  const { atmName, atmTrades, allTrades } = ctx;
  if (!atmName) return [];
  // Resolve trades — scope dispatcher passes filtered atmTrades; tests
  // pass allTrades and rely on suggestAtmEdits filtering.
  const trades = Array.isArray(atmTrades) && atmTrades.length > 0
    ? atmTrades
    : (Array.isArray(allTrades) ? allTrades : []);
  if (trades.length === 0) return [];
  const parsed = resolveParsedAtm(ctx);
  if (!parsed) return [];
  const suggestions = suggestAtmEdits(atmName, parsed, trades);
  return suggestions.map(s => makeObservation({
    id: s.id,
    analyzer: key,
    scope: 'atm',
    scopeId: atmName,
    severity: s.severity === 'warning' ? SEVERITY.WARNING : SEVERITY.INFO,
    category: CATEGORY.EXECUTION,
    title: `${atmName} · ${s.contractLabel}: ${s.reason}`,
    detail: s.detail,
    action: s.expectedImpact
      ? `Suggested: ${s.fieldLabel} ${s.currentValue}${s.unit} → ${s.suggestedValue}${s.unit}. ${s.expectedImpact}`
      : `Suggested: ${s.fieldLabel} ${s.currentValue}${s.unit} → ${s.suggestedValue}${s.unit}.`,
    confidence: s.confidence === 'high' ? 0.85 : s.confidence === 'medium' ? 0.70 : 0.55,
    priority: s.severity === 'warning' ? 75 : 55,
    metric: {
      label: s.fieldLabel,
      value: `${s.currentValue}${s.unit} → ${s.suggestedValue}${s.unit}`,
      baseline: `${s.currentValue}${s.unit}`,
      unit: s.unit,
    },
    evidence: { suggestion: s },
  }));
};

export default { suggestAtmEdits, analyze };
